import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  getClinicalAssessment,
  saveClinicalSoapNotes,
  getClinicalSoapNotes,
  saveClinicalPrescription,
  getClinicalPrescription,
  downloadClinicalAssessmentDocument,
  viewClinicalAssessmentDocument,
} from "../../../api/apiService";
import { getPatientById } from "../../../Services/patientService";
import { formatPatientName, calculateAge, formatDobDisplay } from "../../../utils/patientVisitUtils";
import { sanitizeClinicalNote, sanitizeText } from "../../../utils/inputValidation";
import { registerAbhaProfile } from "../../../api/abhaApi";
import ToastContainer, {
  useToast,
} from "../../../components/common/ToastContainer";
import Calendare from "../../../assets/images/OPD-patients/calendare.svg";
import Time from "../../../assets/images/OPD-patients/time.svg";
import View from "../../../assets/images/OPD-patients/view.svg";
import PatientsIcon from "../../../assets/images/OPD-patients/patients.svg";
import VertalSign from "../../../assets/images/OPD-patients/virtalSign.svg";
import RightCircle from "../../../assets/images/OPD-patients/rightCircle.svg";
import Dangores from "../../../assets/images/OPD-patients/dangoures.svg";
import Link from "../../../assets/images/OPD-patients/link.svg";
import Search from "../../../assets/images/comman/search-outline.svg";
import AbhaBtnIcon from "../../../assets/images/comman/abhaBtnIcon.svg";
import Consent from "../../../assets/images/comman/consent.svg";
import SaveView from "../../../assets/images/comman/saveView.svg";
import LinkAbhaButton from "../../Hospital-Admin/Patients/LinkAbhaButton";
import ConsentRequest from "../../../components/common/ConsentRequest";
import AbhaExternalRecordsModal from "../../../components/common/AbhaExternalRecordsModal";
import MedicineAutocomplete from "../../../components/common/MedicineAutocomplete";
import { useAuth } from "../../../context/AuthContext";
import { PERMISSIONS } from "../../../constants/permissions";

const formatFileSize = (bytes) => {
  if (bytes == null || bytes === "" || isNaN(bytes)) return "";
  const num = Number(bytes);
  if (num <= 0) return "";
  if (num < 1024) return `${num} B`;
  if (num < 1024 * 1024) return `${(num / 1024).toFixed(1)} KB`;
  if (num < 1024 * 1024 * 1024) return `${(num / (1024 * 1024)).toFixed(1)} MB`;
  return `${(num / (1024 * 1024 * 1024)).toFixed(1)} GB`;
};

const OPDDetails = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { toasts, removeToast, showSuccess, showError } = useToast();
  const { can, user } = useAuth();
  const canWritePrescription = can(PERMISSIONS.CLINICAL_PRESCRIPTION_WRITE);
  const canWriteSoap = can(PERMISSIONS.CLINICAL_SOAP_WRITE);
  const [patientData, setPatientData] = useState(null);
  const [showAbhaRecords, setShowAbhaRecords] = useState(false);
  const [medications, setMedications] = useState([
    {
      id: 1,
      drugName: "",
      dosage: "",
      frequency: "",
      form: "Tablet",
      duration: "",
      durationUnit: "days",
      instructions: "",
      snomedCode: "",
      snomedDisplay: "",
    },
  ]);
  const [isLoading, setIsLoading] = useState(false);
  const [nextMedicationId, setNextMedicationId] = useState(2);
  const [soapNotes, setSoapNotes] = useState({
    subjective: "",
    objective: "",
    assessment: "",
    plan: "",
  });
  const [isSavingNotes, setIsSavingNotes] = useState(false);
  const [latestVitals, setLatestVitals] = useState(null);
  const [assessmentDocuments, setAssessmentDocuments] = useState([]);
  const [vitalsLoading, setVitalsLoading] = useState(false);
  const [vitalsError, setVitalsError] = useState(null);

  useEffect(() => {
    let isMounted = true;

    const initPatientData = async () => {
      // Get patient data from location state, or fetch directly via patientId
      let data = location.state?.patientData;

      const queryParams = new URLSearchParams(location.search);
      const patientIdFromQuery =
        queryParams.get("patientId") || location.state?.patientId;
      const targetId = data?._id || data?.id || patientIdFromQuery;

      // If patient data is missing or visits/visitId is missing, fetch rich profile via getPatientById
      if (
        targetId &&
        (!data || !data.visits || data.visits.length === 0 || !data?.visitId)
      ) {
        try {
          const richPatient = await getPatientById(targetId);
          if (richPatient && isMounted) {
            data = {
              ...(data || {}),
              ...richPatient,
              visitId:
                richPatient.visitId ||
                richPatient.visits?.[0]?.visitId ||
                data?.visitId,
            };
          }
        } catch (err) {
          console.warn("Could not fetch rich patient record in OPDDetails:", err);
        }
      }

      if (!isMounted) return;

      if (data) {
        const rawName =
          (data.name && typeof data.name === "string" && data.name.trim() && data.name.trim() !== "undefined undefined")
            ? data.name.trim()
            : (data.fullName && typeof data.fullName === "string" && data.fullName.trim() && data.fullName.trim() !== "undefined undefined")
            ? data.fullName.trim()
            : formatPatientName(data) || "Patient";

        const isAbhaLinked = Boolean(
          data.isAbhaLinked === true ||
          data.isabhalinked === true ||
          data.isABHALinked === true ||
          String(data.isAbhaLinked).toLowerCase() === "true" ||
          String(data.isabhalinked).toLowerCase() === "true" ||
          (data.maskedAbha && String(data.maskedAbha).trim() && data.maskedAbha !== "N/A") ||
          (data.ABHANumber && String(data.ABHANumber).trim()) ||
          (data.abhaaddress && String(data.abhaaddress).trim()) ||
          (data.abhaAddress && String(data.abhaAddress).trim()) ||
          (data.ABHAAddress && String(data.ABHAAddress).trim())
        );

        const dobFormatted = formatDobDisplay(data.dob || data.dateOfBirth);
        const computedAgeNum = calculateAge(dobFormatted || data.dob);
        const calculatedAge =
          computedAgeNum > 0
            ? computedAgeNum
            : (data.age && !isNaN(Number(data.age)) && Number(data.age) > 0
                ? Number(data.age)
                : (data.calculatedAge || ""));

        const maskedAbha =
          data.maskedAbha ||
          (data.ABHANumber ? `**** ${String(data.ABHANumber).slice(-4)}` : "") ||
          data.formattedAbhaId ||
          (data.abhaaddress || data.abhaAddress || data.ABHAAddress || "");

        const enrichedData = {
          ...data,
          name: rawName,
          fullName: rawName,
          calculatedAge,
          age: calculatedAge,
          isAbhaLinked,
          isabhalinked: isAbhaLinked,
          maskedAbha,
          formattedAbhaId: maskedAbha || data.ABHANumber || "",
        };

        setPatientData(enrichedData);
        fetchLatestVitals(enrichedData);
        fetchSoapNotes(enrichedData);
        fetchPrescription(enrichedData);
      } else {
        // If no patient data is available, redirect back to patients list
        console.warn("No patient data available, redirecting to patients list");
        navigate("/patients");
      }
    };

    initPatientData();

    return () => {
      isMounted = false;
    };
  }, [location.state, location.search, navigate]);

  // Fetch vitals from GET /visit/:visitId/clinical/assessment only (no old ABHA vitals API)
  const fetchLatestVitals = async (patientInfo) => {
    if (!patientInfo) return;

    const visitId =
      patientInfo.visitId || patientInfo.visits?.[0]?.visitId || "";
    const patientId = patientInfo._id || patientInfo.id || "";

    setVitalsLoading(true);
    setVitalsError(null);

    try {
      if (!visitId || !patientId) {
        setVitalsLoading(false);
        return;
      }

      const response = await getClinicalAssessment(visitId, patientId);
      const payload = response?.data || response?.assessment || response;
      const vitals = payload?.vitals;

      if (Array.isArray(payload?.documentUploads)) {
        setAssessmentDocuments(payload.documentUploads);
      } else {
        setAssessmentDocuments([]);
      }

      if (
        vitals &&
        typeof vitals === "object" &&
        Object.keys(vitals).length > 0
      ) {
        const parsedVitals = {
          height: vitals.height,
          weight: vitals.weight,
          bmi: vitals.bmi,
          bmiCategory: vitals.category || vitals.bmiCategory,
          pulse: vitals.pulse,
          pulseRate: vitals.pulse,
          sbp: vitals.sbp,
          dbp: vitals.dbp,
          bloodPressure:
            vitals.sbp != null && vitals.dbp != null
              ? `${vitals.sbp}/${vitals.dbp}`
              : undefined,
          temperature: vitals.temp ?? vitals.temperature,
          spo2: vitals.spo2,
          spO2: vitals.spo2,
        };
        setLatestVitals(parsedVitals);
      } else {
        setLatestVitals(null);
        setVitalsError("No vitals data found");
      }
    } catch (error) {
      console.error("Error fetching vitals:", error);
      setLatestVitals(null);
      setVitalsError("Failed to fetch vitals data");
    } finally {
      setVitalsLoading(false);
    }
  };

  // Load existing SOAP notes from GET /visit/:visitId/clinical/soap-notes
  const fetchSoapNotes = async (patientInfo) => {
    if (!patientInfo) return;
    const visitId =
      patientInfo.visitId || patientInfo.visits?.[0]?.visitId || "";
    const patientId = patientInfo._id || patientInfo.id || "";
    if (!visitId || !patientId) return;
    try {
      const response = await getClinicalSoapNotes(visitId, patientId);
      const payload = response?.data ?? response ?? {};
      if (
        payload.subjective != null ||
        payload.objective != null ||
        payload.assessment != null ||
        payload.plan != null
      ) {
        setSoapNotes({
          subjective: payload.subjective ?? "",
          objective: payload.objective ?? "",
          assessment: payload.assessment ?? "",
          plan: payload.plan ?? "",
        });
      }
    } catch (err) {
      console.error("Error fetching SOAP notes:", err);
    }
  };

  // Load existing prescription from GET /visit/:visitId/clinical/prescription
  const fetchPrescription = async (patientInfo) => {
    if (!patientInfo) return;
    const visitId =
      patientInfo.visitId || patientInfo.visits?.[0]?.visitId || "";
    const patientId = patientInfo._id || patientInfo.id || "";
    if (!visitId || !patientId) return;
    try {
      const response = await getClinicalPrescription(visitId, patientId);
      const list =
        response?.data?.medications ??
        response?.medications ??
        (Array.isArray(response?.data) ? response.data : []);
      if (Array.isArray(list) && list.length > 0) {
        const mapped = list.map((med, index) => ({
          id: index + 1,
          drugName: med.medicine ?? med.drugName ?? med.drug_name ?? "",
          dosage: med.dosage ?? "",
          frequency: med.frequency ?? "",
          form: med.form ?? "Tablet",
          duration: med.duration ?? "",
          durationUnit: med.duration_unit ?? med.durationUnit ?? "days",
          instructions: med.instructions ?? "",
          snomedCode: med.snomed_code ?? med.snomedCode ?? "",
          snomedDisplay:
            med.snomed_display ??
            med.snomedDisplay ??
            med.medicine ??
            med.drugName ??
            "",
        }));
        setMedications(mapped);
        setNextMedicationId(mapped.length + 1);
      }
    } catch (err) {
      console.error("Error fetching prescription:", err);
    }
  };

  // Helper function to safely get value or return fallback
  const safeGet = (value, fallback = "") => value || fallback;

  // Helper function to format date
  const formatDate = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return "";
    const options = { day: "2-digit", month: "short", year: "numeric" };
    return date.toLocaleDateString("en-GB", options);
  };

  // Helper function to format time
  const formatTime = (timeString) => {
    if (!timeString) return "";
    if (
      typeof timeString === "string" &&
      (timeString.includes("AM") ||
        timeString.includes("PM") ||
        timeString.includes("am") ||
        timeString.includes("pm"))
    ) {
      return timeString;
    }
    try {
      const date = new Date(timeString);
      return date.toLocaleTimeString("en-US", {
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
      });
    } catch (e) {
      return timeString;
    }
  };

  // Function to add new medication form
  const addNewMedication = () => {
    const newMedication = {
      id: nextMedicationId,
      drugName: "",
      dosage: "",
      frequency: "",
      form: "Tablet",
      duration: "",
      durationUnit: "days",
      instructions: "",
      snomedCode: "",
      snomedDisplay: "",
    };
    setMedications([...medications, newMedication]);
    setNextMedicationId(nextMedicationId + 1);
  };

  // Function to remove medication form
  const removeMedication = (id) => {
    if (medications.length > 1) {
      setMedications(medications.filter((med) => med.id !== id));
    }
  };

  // Function to update medication data
  const updateMedication = (id, field, value) => {
    setMedications((prev) =>
      prev.map((med) => (med.id === id ? { ...med, [field]: value } : med)),
    );
  };

  // Helper function to map frequency to structured timing
  const getTimingFromFrequency = (frequency) => {
    const mapping = {
      "Once daily": { frequency: 1, period: 1, periodUnit: "d" },
      "Twice daily": { frequency: 2, period: 1, periodUnit: "d" },
      "Thrice daily": { frequency: 3, period: 1, periodUnit: "d" },
      "Four times daily": { frequency: 4, period: 1, periodUnit: "d" },
      "Every 4 hours": { frequency: 1, period: 4, periodUnit: "h" },
      "Every 6 hours": { frequency: 1, period: 6, periodUnit: "h" },
      "Every 8 hours": { frequency: 1, period: 8, periodUnit: "h" },
      "Every 12 hours": { frequency: 1, period: 12, periodUnit: "h" },
      "Once a week": { frequency: 1, period: 1, periodUnit: "wk" },
      "Alternate days": { frequency: 1, period: 2, periodUnit: "d" },
      "At bedtime": { frequency: 1, period: 1, periodUnit: "d" },
      "As needed (SOS)": { frequency: 1, period: 1, periodUnit: "d" },
      "As needed": { frequency: 1, period: 1, periodUnit: "d" }, // Legacy compatibility
    };
    return mapping[frequency] || null;
  };

  // Function to save medications via visit clinical prescription API
  const saveMedications = async (e) => {
    if (e) e.preventDefault(); // Prevent form submission from reloading the page
    if (!canWritePrescription) {
      showError("You do not have permission to prescribe medications.");
      return;
    }
    const visitId =
      patientData.visitId || patientData.visits?.[0]?.visitId || "";
    const patientId = patientData._id || patientData.id || "";

    if (!visitId) {
      showError("Visit ID is missing. Cannot save prescription.");
      return;
    }

    setIsLoading(true);
    try {
      const prescriptionInput = medications
        .filter((med) => (med.medicine ?? med.drugName ?? "").trim() !== "" && (med.dosage ?? "").trim() !== "")
        .map((med) => ({
          medicine: sanitizeText(med.medicine ?? med.drugName),
          dosage: sanitizeText(med.dosage),
          form: med.form || undefined,
          frequency: med.frequency,
          timing: getTimingFromFrequency(med.frequency) || undefined,
          duration: med.duration ? sanitizeText(med.duration) : undefined,
          durationUnit: med.durationUnit || undefined,
          instructions: med.instructions ? sanitizeText(med.instructions) : undefined,
          prescribedBy: "Dr. Smith", // TODO: replace with actual doctor name
          ...(med.snomedCode
            ? { snomedCode: med.snomedCode, snomedDisplay: med.snomedDisplay }
            : {}),
        }));

      const body = { medications: prescriptionInput };

      let prescriptionResponse;
      try {
        prescriptionResponse = await saveClinicalPrescription(
          visitId,
          body,
          patientId || undefined,
        );
      } catch (err) {
        const msg =
          err.response?.data?.message ||
          err.message ||
          "Failed to save prescription.";
        showError(msg);
        setIsLoading(false);
        return;
      }

      if (
        prescriptionResponse?.status === "success" ||
        prescriptionResponse?.success !== false
      ) {
        showSuccess(
          prescriptionResponse?.message || "Prescription saved successfully",
        );
      } else {
        showError(
          prescriptionResponse?.message || "Failed to save prescription.",
        );
      }
    } catch (error) {
      console.error("Error saving medications:", error);
      showError(`Failed to save medications: ${error.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  // Function to save SOAP notes via ABHA API
  const saveNotes = async () => {
    if (!canWriteSoap) {
      showError("You do not have permission to save clinical SOAP notes.");
      return;
    }
    const visitId =
      patientData.visitId || patientData.visits?.[0]?.visitId || "";
    const patientId = patientData._id || patientData.id || "";

    if (!visitId) {
      showError("Visit ID is missing. Cannot save SOAP notes.");
      return;
    }

    setIsSavingNotes(true);
    try {
      // POST /visit/:visitId/clinical/soap-notes (body: subjective, objective, assessment, plan)
      const body = {
        subjective: sanitizeClinicalNote(soapNotes.subjective ?? ""),
        objective: sanitizeClinicalNote(soapNotes.objective ?? ""),
        assessment: sanitizeClinicalNote(soapNotes.assessment ?? ""),
        plan: sanitizeClinicalNote(soapNotes.plan ?? ""),
      };

      let soapNotesResponse;
      try {
        soapNotesResponse = await saveClinicalSoapNotes(
          visitId,
          body,
          patientId || undefined,
        );
      } catch (err) {
        const msg =
          err.response?.data?.message ||
          err.message ||
          "Failed to save SOAP notes.";
        showError(msg);
        setIsSavingNotes(false);
        return;
      }

      if (
        soapNotesResponse?.status === "success" ||
        soapNotesResponse?.success !== false
      ) {
        showSuccess(
          soapNotesResponse?.message || "SOAP notes saved successfully",
        );
      } else {
        showError(soapNotesResponse?.message || "Failed to save SOAP notes.");
      }
    } catch (error) {
      console.error("Error saving SOAP notes:", error);
      showError(`Failed to save SOAP notes: ${error.message}`);
    } finally {
      setIsSavingNotes(false);
    }
  };

  if (!patientData) {
    return (
      <div className="container-fluid">
        <div className="text-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="mt-2">Loading patient details...</p>
        </div>
      </div>
    );
  }
  return (
    <>
      <div className="container-fluid">
        <div className="opdDetailsPage">
          {/* Back Button */}
          <div className="mb-3">
            <button
              className="customeBtn btnEmpty"
              onClick={() => navigate("/patients")}
              style={{ textDecoration: "none" }}
            >
              ← Back to Patients
            </button>
          </div>
          <div className="allMainFeilds">
            <div className="basicDetailsUser">
              <div className="leftSide">
                <h4>
                  {safeGet(patientData.fullName || patientData.name, "Patient")}
                </h4>
                <ul>
                  <li className="years">
                    {safeGet(patientData.calculatedAge || patientData.age)}{" "}
                    {patientData.calculatedAge || patientData.age
                      ? "years"
                      : ""}
                  </li>
                  <li className="age">{safeGet(patientData.gender)}</li>
                  <li className="abha">
                    ABHA:{" "}
                    {safeGet(
                      patientData.formattedAbhaId ||
                        patientData.maskedAbha ||
                        patientData.ABHANumber,
                      "N/A",
                    )}
                    {(patientData.ABHAAddress ||
                      patientData.abhaaddress ||
                      patientData.abhaAddress) && (
                      <span
                        className="ms-2 text-muted"
                        style={{ fontWeight: "normal", fontSize: "0.85em" }}
                      >
                        ·{" "}
                        {patientData.ABHAAddress ||
                          patientData.abhaaddress ||
                          patientData.abhaAddress}
                      </span>
                    )}
                  </li>
                  <li className="visit">
                    Visit:{" "}
                    {safeGet(
                      patientData.visitId || patientData._id?.slice(-8),
                      "N/A",
                    )}
                  </li>
                </ul>
              </div>
              <div className="rightSide">
                <div className="rideSideActionView">
                  <div className="years">
                    <img src={Calendare} />{" "}
                    {formatDate(
                      patientData.appointmentDate ||
                        patientData.updatedAt ||
                        patientData.createdAt,
                    )}
                  </div>
                  <div className="time">
                    <img src={Time} />{" "}
                    {formatTime(patientData.appointmentTime) || "Not scheduled"}
                  </div>
                  {/* <div className="viewHistory">
                    <button className="customeBtn btnWhite">
                      <img src={View} /> View History
                    </button>
                  </div> */}
                  {!(
                    patientData?.isAbhaLinked === true ||
                    patientData?.isabhalinked === true ||
                    patientData?.isABHALinked === true ||
                    String(patientData?.isAbhaLinked).toLowerCase() === "true" ||
                    String(patientData?.isabhalinked).toLowerCase() === "true" ||
                    (patientData?.formattedAbhaId &&
                      patientData?.formattedAbhaId !== "N/A") ||
                    (patientData?.maskedAbha &&
                      patientData?.maskedAbha !== "N/A") ||
                    (patientData?.ABHANumber &&
                      String(patientData.ABHANumber).trim()) ||
                    (patientData?.abhaaddress &&
                      String(patientData.abhaaddress).trim()) ||
                    (patientData?.abhaAddress &&
                      String(patientData.abhaAddress).trim()) ||
                    (patientData?.ABHAAddress &&
                      String(patientData.ABHAAddress).trim())
                  ) && <LinkAbhaButton patient={patientData} />}
                  <div className="abhaBtn">
                    <button
                      className="customeBtn darkBlue"
                      onClick={() => setShowAbhaRecords(true)}
                    >
                      <img src={AbhaBtnIcon} style={{ width: "20px" }} /> ABHA
                    </button>
                  </div>
                </div>
              </div>
              <div className="actionTwoBtn">
                <button
                  className="customeBtn btnLightBlue"
                  data-bs-toggle="offcanvas"
                  data-bs-target="#offcanvasRight"
                  aria-controls="offcanvasRight"
                >
                  <img src={Consent} /> Consent Request
                </button>
                <ConsentRequest />
                <AbhaExternalRecordsModal
                  show={showAbhaRecords}
                  onClose={() => setShowAbhaRecords(false)}
                  patientId={
                    patientData?.abhaAddress ||
                    patientData?.ABHAAddress ||
                    patientData?.abhaaddress ||
                    patientData?.formattedAbhaId
                  }
                  patientData={patientData}
                />
                {/* &nbsp;&nbsp;
                <button className="customeBtn btnLightGreen">
                  <img src={SaveView} /> Save and View
                </button> */}
              </div>
            </div>

            <div className="patientSumrySection">
              <div className="row">
                <div className="col-xs-12 col-md-4">
                  <div className="patientLeftSection">
                    <div className="patSummryBox">
                      <div className="patSumHeader">
                        <div className="left">
                          <h4>
                            <img src={PatientsIcon} />
                            Patient Summary
                          </h4>
                        </div>
                        <div className="right"></div>
                      </div>
                      <div className="patSumDetails">
                        <div className="patSumDetCol">
                          <h6>DOB:</h6>
                          <h4>
                            {formatDobDisplay(patientData.dob) || safeGet(patientData.dob, "Not specified")}
                          </h4>
                        </div>
                        <div className="patSumDetCol">
                          <h6>Blood Group:</h6>
                          <h4>
                            {safeGet(patientData.bloodGroup, "Not specified")}
                          </h4>
                        </div>
                        <div className="patSumDetCol">
                          <h6>Phone:</h6>
                          <h4>{safeGet(patientData.mobile, "Not provided")}</h4>
                        </div>
                        <div className="patSumDetCol">
                          <h6>Email:</h6>
                          <h4>{safeGet(patientData.email, "Not provided")}</h4>
                        </div>
                        <div className="patSumDetCol">
                          <h6>Emergency Contact:</h6>
                          <h4>
                            {safeGet(
                              patientData.emergencyContact,
                              "Not provided",
                            )}
                          </h4>
                        </div>
                        <div className="patSumDetCol">
                          <h6>Address:</h6>
                          <h4>
                            {safeGet(patientData.address, "Not provided")}
                          </h4>
                        </div>
                      </div>
                      <div className="symptonesSign">
                        <div className="headersSym">
                          <h4>
                            <img src={Dangores} /> Allergies
                          </h4>
                        </div>
                        <div className="symptonesSignBtn">
                          {patientData.allergies &&
                          patientData.allergies.length > 0 ? (
                            Array.isArray(patientData.allergies) ? (
                              patientData.allergies.map((allergy, index) => (
                                <span key={index} className="badge bg-light text-dark border me-1">{allergy}</span>
                              ))
                            ) : typeof patientData.allergies === "string" ? (
                              patientData.allergies
                                .split(",")
                                .map((allergy, index) => (
                                  <span key={index} className="badge bg-light text-dark border me-1">{allergy.trim()}</span>
                                ))
                            ) : null
                          ) : (
                            <span className="text-muted">
                              No known allergies
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="ChronicConditions">
                        <div className="ChrCondHeader">
                          <h4>Chronic Conditions</h4>
                        </div>
                        <div className="ChronicConditionsDetails">
                          {patientData.chronicConditions &&
                          patientData.chronicConditions.length > 0 ? (
                            patientData.chronicConditions.map(
                              (condition, index) => (
                                <span key={index}>{condition}</span>
                              ),
                            )
                          ) : (
                            <span className="text-muted">
                              No chronic conditions recorded
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    <div
                      className="patSummryBox"
                      style={{ cursor: "pointer" }}
                      onClick={() =>
                        navigate("/opd-assessment", {
                          state: {
                            patientData: patientData,
                          },
                        })
                      }
                      title="Click to view/edit vital signs"
                    >
                      <div className="patSumHeader">
                        <div className="left">
                          <h4>
                            <img src={VertalSign} />
                            Vital Signs
                          </h4>
                          {vitalsLoading && (
                            <small className="text-muted"> (Loading...)</small>
                          )}
                          {vitalsError && (
                            <small className="text-danger">
                              {" "}
                              ({vitalsError})
                            </small>
                          )}
                        </div>
                      </div>
                      <div className="patSumDetails">
                        <div className="patSumDetCol">
                          <h6>Height</h6>
                          <h4>
                            {safeGet(
                              latestVitals?.height || patientData.height,
                            ) || "-"}
                          </h4>
                          <p>cm</p>
                          {latestVitals?.height && (
                            <small className="text-success">Latest</small>
                          )}
                        </div>
                        <div className="patSumDetCol">
                          <h6>Weight</h6>
                          <h4>
                            {safeGet(
                              latestVitals?.weight || patientData.weight,
                            ) || "-"}
                          </h4>
                          <p>kg</p>
                          {latestVitals?.weight && (
                            <small className="text-success">Latest</small>
                          )}
                        </div>
                        <div className="patSumDetCol">
                          <h6>BMI</h6>
                          <h4>
                            {safeGet(latestVitals?.bmi || patientData.bmi) ||
                              "-"}
                          </h4>
                          {(latestVitals?.bmiCategory ||
                            patientData.bmiCategory) && (
                            <span
                              className={`${(latestVitals?.bmiCategory || patientData.bmiCategory).toLowerCase().replace(" ", "")}`}
                            >
                              {latestVitals?.bmiCategory ||
                                patientData.bmiCategory}
                            </span>
                          )}
                          {latestVitals?.bmi && (
                            <small className="text-success d-block">
                              Latest
                            </small>
                          )}
                        </div>
                      </div>
                      <div className="reportsVirtalSign">
                        <div className="reportsVirtalSignRow">
                          <div className="leftVitalTitle">
                            <h4>Blood Pressure</h4>
                            {(latestVitals?.sbp || latestVitals?.dbp) && (
                              <small className="text-success">Latest</small>
                            )}
                          </div>
                          <div className="rightVitalVal">
                            <h6>
                              {latestVitals?.sbp && latestVitals?.dbp
                                ? `${latestVitals.sbp}/${latestVitals.dbp}`
                                : safeGet(
                                    latestVitals?.bloodPressure ||
                                      patientData.bloodPressure,
                                  ) || "-/-"}
                            </h6>
                            <span>mmHg</span>
                            {(latestVitals?.bloodPressureStatus ||
                              patientData.bloodPressureStatus) && (
                              <img
                                src={
                                  (latestVitals?.bloodPressureStatus ||
                                    patientData.bloodPressureStatus) ===
                                  "normal"
                                    ? RightCircle
                                    : Dangores
                                }
                              />
                            )}
                          </div>
                        </div>
                        <div className="reportsVirtalSignRow">
                          <div className="leftVitalTitle">
                            <h4>Pulse Rate</h4>
                            {latestVitals?.pulse && (
                              <small className="text-success">Latest</small>
                            )}
                          </div>
                          <div className="rightVitalVal">
                            <h4>
                              {safeGet(
                                latestVitals?.pulse ||
                                  latestVitals?.pulseRate ||
                                  patientData.pulseRate ||
                                  patientData.pulse,
                              ) || "-"}
                            </h4>
                            <span>bpm</span>
                            {(latestVitals?.pulseRateStatus ||
                              patientData.pulseRateStatus) && (
                              <img
                                src={
                                  (latestVitals?.pulseRateStatus ||
                                    patientData.pulseRateStatus) === "normal"
                                    ? RightCircle
                                    : Dangores
                                }
                              />
                            )}
                          </div>
                        </div>
                        <div className="reportsVirtalSignRow">
                          <div className="leftVitalTitle">
                            <h4>Temperature</h4>
                            {latestVitals?.temperature && (
                              <small className="text-success">Latest</small>
                            )}
                          </div>
                          <div className="rightVitalVal">
                            <h4>
                              {safeGet(
                                latestVitals?.temperature ||
                                  patientData.temperature,
                              ) || "-"}
                            </h4>
                            <span>°F</span>
                            {(latestVitals?.temperatureStatus ||
                              patientData.temperatureStatus) && (
                              <img
                                src={
                                  (latestVitals?.temperatureStatus ||
                                    patientData.temperatureStatus) === "normal"
                                    ? RightCircle
                                    : Dangores
                                }
                              />
                            )}
                          </div>
                        </div>
                        <div className="reportsVirtalSignRow">
                          <div className="leftVitalTitle">
                            <h4>SpO2</h4>
                            {latestVitals?.spo2 && (
                              <small className="text-success">Latest</small>
                            )}
                          </div>
                          <div className="rightVitalVal">
                            <h4>
                              {safeGet(
                                latestVitals?.spo2 ||
                                  latestVitals?.spO2 ||
                                  patientData.spO2 ||
                                  patientData.spo2,
                              ) || "-"}
                            </h4>
                            <span>%</span>
                            {(latestVitals?.spO2Status ||
                              patientData.spO2Status) && (
                              <img
                                src={
                                  (latestVitals?.spO2Status ||
                                    patientData.spO2Status) === "normal"
                                    ? RightCircle
                                    : Dangores
                                }
                              />
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                    {assessmentDocuments.length > 0 && (
                      <div className="reportsVirtalSignSection mt-3 p-3 bg-white border rounded shadow-sm">
                        <div className="d-flex align-items-center justify-content-between mb-2 pb-2 border-bottom">
                          <h5 className="mb-0 fw-semibold text-dark" style={{ fontSize: "14px" }}>
                            Assessment Documents ({assessmentDocuments.length})
                          </h5>
                        </div>
                        <div className="d-flex flex-column gap-2">
                          {assessmentDocuments.map((doc, idx) => (
                            <div
                              key={idx}
                              className="d-flex align-items-center justify-content-between p-2 border rounded bg-light"
                              style={{ fontSize: "12px" }}
                            >
                              <div
                                className="d-flex align-items-center gap-2 text-truncate"
                                style={{ maxWidth: "60%" }}
                              >
                                <svg
                                  width="16"
                                  height="16"
                                  viewBox="0 0 24 24"
                                  fill="none"
                                  stroke="#4F46E5"
                                  strokeWidth="2"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  style={{ flexShrink: 0 }}
                                >
                                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                                  <polyline points="14 2 14 8 20 8"></polyline>
                                  <line x1="16" y1="13" x2="8" y2="13"></line>
                                  <line x1="16" y1="17" x2="8" y2="17"></line>
                                  <polyline points="10 9 9 9 8 9"></polyline>
                                </svg>
                                {(() => {
                                  const originalDocName =
                                    (doc.originalname ||
                                      doc.originalName ||
                                      doc.name ||
                                      doc.fileName ||
                                      doc.filename ||
                                      "")
                                      .replace(/^(?:files-)?\d{10,14}[-_]/, "")
                                      .replace(/^[a-f0-9]{24,32}[-_]/, "") ||
                                    `Document ${idx + 1}`;
                                  const sizeStr = formatFileSize(doc.size);
                                  return (
                                    <div className="text-truncate">
                                      <span
                                        className="fw-medium text-dark text-truncate d-block"
                                        title={originalDocName}
                                      >
                                        {originalDocName}
                                      </span>
                                      {(doc.uploadDate || sizeStr) && (
                                        <div
                                          className="d-flex align-items-center gap-1 text-muted"
                                          style={{ fontSize: "11px" }}
                                        >
                                          {doc.uploadDate && (
                                            <span>
                                              {new Date(doc.uploadDate).toLocaleDateString("en-GB", {
                                                day: "2-digit",
                                                month: "short",
                                                year: "numeric",
                                              })}
                                            </span>
                                          )}
                                          {doc.uploadDate && sizeStr && <span>&bull;</span>}
                                          {sizeStr && <span>{sizeStr}</span>}
                                        </div>
                                      )}
                                    </div>
                                  );
                                })()}
                              </div>
                              <div className="d-flex align-items-center gap-1">
                                <button
                                  type="button"
                                  className="btn btn-sm btn-outline-primary py-0 px-2"
                                  style={{ fontSize: "11px", height: "24px" }}
                                  onClick={async () => {
                                    const vId =
                                      patientData.visitId ||
                                      patientData.visits?.[0]?.visitId ||
                                      "";
                                    const pId =
                                      patientData._id || patientData.id || "";
                                    try {
                                      await viewClinicalAssessmentDocument(
                                        vId,
                                        idx,
                                        pId || undefined
                                      );
                                    } catch (err) {
                                      console.error("Failed to view document:", err);
                                    }
                                  }}
                                >
                                  View
                                </button>
                                <button
                                  type="button"
                                  className="btn btn-sm btn-outline-secondary py-0 px-2"
                                  style={{ fontSize: "11px", height: "24px" }}
                                  onClick={async () => {
                                    const vId =
                                      patientData.visitId ||
                                      patientData.visits?.[0]?.visitId ||
                                      "";
                                    const pId =
                                      patientData._id || patientData.id || "";
                                    const origName =
                                      (doc.originalname ||
                                        doc.originalName ||
                                        doc.name ||
                                        doc.fileName ||
                                        doc.filename ||
                                        "")
                                        .replace(/^(?:files-)?\d{10,14}[-_]/, "")
                                        .replace(/^[a-f0-9]{24,32}[-_]/, "");
                                    try {
                                      await downloadClinicalAssessmentDocument(
                                        vId,
                                        idx,
                                        pId || undefined,
                                        origName
                                      );
                                    } catch (err) {
                                      console.error("Failed to download document:", err);
                                    }
                                  }}
                                >
                                  Download
                                </button>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
                <div className="col-xs-12 col-md-8">
                  <div className="patientRightSection">
                    <div className="patientTabsSection">
                      <ul className="nav nav-pills" role="tablist">
                        <li className="nav-item">
                          <a
                            className="nav-link active"
                            data-bs-toggle="pill"
                            href="#All"
                          >
                            <svg
                              width={16}
                              height={17}
                              viewBox="0 0 16 17"
                              fill="none"
                              xmlns="http://www.w3.org/2000/svg"
                            >
                              <path
                                d="M9.99984 1.8335H3.99984C3.64622 1.8335 3.30708 1.97397 3.05703 2.22402C2.80698 2.47407 2.6665 2.81321 2.6665 3.16683V13.8335C2.6665 14.1871 2.80698 14.5263 3.05703 14.7763C3.30708 15.0264 3.64622 15.1668 3.99984 15.1668H11.9998C12.3535 15.1668 12.6926 15.0264 12.9426 14.7763C13.1927 14.5263 13.3332 14.1871 13.3332 13.8335V5.16683L9.99984 1.8335Z"
                                stroke="#1F2A37"
                                strokeWidth="1.33333"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                              <path
                                d="M9.3335 1.8335V4.50016C9.3335 4.85378 9.47397 5.19292 9.72402 5.44297C9.97407 5.69302 10.3132 5.8335 10.6668 5.8335H13.3335"
                                stroke="#1F2A37"
                                strokeWidth="1.33333"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                              <path
                                d="M6.66683 6.5H5.3335"
                                stroke="#1F2A37"
                                strokeWidth="1.33333"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                              <path
                                d="M10.6668 9.1665H5.3335"
                                stroke="#1F2A37"
                                strokeWidth="1.33333"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                              <path
                                d="M10.6668 11.8335H5.3335"
                                stroke="#1F2A37"
                                strokeWidth="1.33333"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                            </svg>
                            SOAP Notes
                          </a>
                        </li>
                        <li className="nav-item">
                          <a
                            className="nav-link"
                            data-bs-toggle="pill"
                            href="#EmergencyCases"
                          >
                            <svg
                              width={20}
                              height={20}
                              viewBox="0 0 20 20"
                              fill="none"
                              xmlns="http://www.w3.org/2000/svg"
                            >
                              <path
                                d="M18.3332 9.99984H16.2665C15.9023 9.99906 15.5479 10.1176 15.2574 10.3373C14.967 10.557 14.7565 10.8658 14.6582 11.2165L12.6998 18.1832C12.6872 18.2264 12.6609 18.2645 12.6248 18.2915C12.5888 18.3186 12.5449 18.3332 12.4998 18.3332C12.4548 18.3332 12.4109 18.3186 12.3748 18.2915C12.3388 18.2645 12.3125 18.2264 12.2998 18.1832L7.69984 1.8165C7.68722 1.77323 7.6609 1.73522 7.62484 1.70817C7.58878 1.68112 7.54491 1.6665 7.49984 1.6665C7.45476 1.6665 7.4109 1.68112 7.37484 1.70817C7.33878 1.73522 7.31246 1.77323 7.29984 1.8165L5.3415 8.78317C5.24356 9.13247 5.03432 9.44027 4.74556 9.65985C4.45679 9.87943 4.10427 9.9988 3.7415 9.99984H1.6665"
                                stroke="#1C64F2"
                                strokeWidth="1.66667"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                            </svg>
                            Prescription
                          </a>
                        </li>
                        <li className="nav-item">
                          <a
                            className="nav-link"
                            data-bs-toggle="pill"
                            href="#Consultation"
                          >
                            <svg
                              width={20}
                              height={20}
                              viewBox="0 0 20 20"
                              fill="none"
                              xmlns="http://www.w3.org/2000/svg"
                            >
                              <path
                                d="M18.3332 9.99984H16.2665C15.9023 9.99906 15.5479 10.1176 15.2574 10.3373C14.967 10.557 14.7565 10.8658 14.6582 11.2165L12.6998 18.1832C12.6872 18.2264 12.6609 18.2645 12.6248 18.2915C12.5888 18.3186 12.5449 18.3332 12.4998 18.3332C12.4548 18.3332 12.4109 18.3186 12.3748 18.2915C12.3388 18.2645 12.3125 18.2264 12.2998 18.1832L7.69984 1.8165C7.68722 1.77323 7.6609 1.73522 7.62484 1.70817C7.58878 1.68112 7.54491 1.6665 7.49984 1.6665C7.45476 1.6665 7.4109 1.68112 7.37484 1.70817C7.33878 1.73522 7.31246 1.77323 7.29984 1.8165L5.3415 8.78317C5.24356 9.13247 5.03432 9.44027 4.74556 9.65985C4.45679 9.87943 4.10427 9.9988 3.7415 9.99984H1.6665"
                                stroke="#1C64F2"
                                strokeWidth="1.66667"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                            </svg>
                            Lab & Imaging{" "}
                          </a>
                        </li>
                        <li className="nav-item">
                          <a
                            className="nav-link"
                            data-bs-toggle="pill"
                            href="#MedicalRecords"
                          >
                            <svg
                              width={20}
                              height={20}
                              viewBox="0 0 20 20"
                              fill="none"
                              xmlns="http://www.w3.org/2000/svg"
                            >
                              <path
                                d="M15.8332 17.5V15.8333C15.8332 14.9493 15.482 14.1014 14.8569 13.4763C14.2317 12.8512 13.3839 12.5 12.4998 12.5H7.49984C6.61578 12.5 5.76794 12.8512 5.14281 13.4763C4.51769 14.1014 4.1665 14.9493 4.1665 15.8333V17.5"
                                stroke="#1C64F2"
                                strokeWidth="1.66667"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                              <path
                                d="M9.99984 9.16667C11.8408 9.16667 13.3332 7.67428 13.3332 5.83333C13.3332 3.99238 11.8408 2.5 9.99984 2.5C8.15889 2.5 6.6665 3.99238 6.6665 5.83333C6.6665 7.67428 8.15889 9.16667 9.99984 9.16667Z"
                                stroke="#1C64F2"
                                strokeWidth="1.66667"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                            </svg>
                            Referral{" "}
                          </a>
                        </li>
                        <li className="nav-item">
                          <a
                            className="nav-link"
                            data-bs-toggle="pill"
                            href="#DischargeSummary"
                          >
                            <svg
                              width={16}
                              height={16}
                              viewBox="0 0 16 16"
                              fill="none"
                              xmlns="http://www.w3.org/2000/svg"
                            >
                              <path
                                d="M14.5341 6.66666C14.8385 8.16086 14.6215 9.71428 13.9193 11.0679C13.2171 12.4214 12.072 13.4934 10.6751 14.1049C9.27816 14.7164 7.71382 14.8305 6.24293 14.4282C4.77205 14.026 3.48353 13.1316 2.59225 11.8943C1.70097 10.657 1.26081 9.15148 1.34518 7.62892C1.42954 6.10635 2.03332 4.65872 3.05583 3.52744C4.07835 2.39616 5.45779 1.64961 6.96411 1.4123C8.47043 1.17498 10.0126 1.46123 11.3334 2.22333"
                                stroke="#0E9F6E"
                                strokeWidth="1.33333"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                              <path
                                d="M6 7.33366L8 9.33366L14.6667 2.66699"
                                stroke="#0E9F6E"
                                strokeWidth="1.33333"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                            </svg>
                            ABDM{" "}
                          </a>
                        </li>
                      </ul>
                      <div className="tab-content">
                        <div id="All" className="container tab-pane active">
                          <br />
                          <div className="soapNotesSection">
                            <div className="soapNotesHeader">
                              <div className="soapLeftHeader">
                                <h4>
                                  <img src={View} /> SOAP Notes
                                </h4>
                              </div>
                              <div className="soapRightHeader">
                                {canWriteSoap && (
                                  <button
                                    onClick={saveNotes}
                                    disabled={isSavingNotes}
                                    style={{
                                      opacity: isSavingNotes ? 0.6 : 1,
                                      cursor: isSavingNotes
                                        ? "not-allowed"
                                        : "pointer",
                                    }}
                                  >
                                    {isSavingNotes ? "Saving..." : "Save Notes"}
                                  </button>
                                )}
                              </div>
                            </div>
                            <div className="soapNotesRowSec" style={{ marginTop: "16px" }}>
                              <div className="sopRowHeader">
                                <h4>Subjective</h4>
                                <p>What the patient tells you</p>
                              </div>
                              <div className="sopRowBody">
                                {!canWriteSoap ? (
                                  <div
                                    style={{
                                      minHeight: "72px",
                                      padding: "12px 16px",
                                      backgroundColor: "#f9fafb",
                                      border: "1px solid #e5e7eb",
                                      borderRadius: "8px",
                                      color: soapNotes.subjective ? "#1f2937" : "#9ca3af",
                                      fontSize: "14px",
                                      lineHeight: "1.6",
                                      whiteSpace: "pre-wrap",
                                      wordBreak: "break-word",
                                    }}
                                  >
                                    {soapNotes.subjective || <span style={{ fontStyle: "italic", color: "#9ca3af" }}>No notes recorded</span>}
                                  </div>
                                ) : (
                                  <textarea
                                    type="text"
                                    rows="4"
                                    placeholder="Patient complaints, symptoms, and medical history..."
                                    value={soapNotes.subjective}
                                    onChange={(e) =>
                                      setSoapNotes({
                                        ...soapNotes,
                                        subjective: e.target.value,
                                      })
                                    }
                                  ></textarea>
                                )}
                              </div>
                            </div>
                            <div className="soapNotesRowSec">
                              <div className="sopRowHeader">
                                <h4>Objective</h4>
                                <p>What you observe and measure</p>
                              </div>
                              <div className="sopRowBody">
                                {!canWriteSoap ? (
                                  <div
                                    style={{
                                      minHeight: "72px",
                                      padding: "12px 16px",
                                      backgroundColor: "#f9fafb",
                                      border: "1px solid #e5e7eb",
                                      borderRadius: "8px",
                                      color: soapNotes.objective ? "#1f2937" : "#9ca3af",
                                      fontSize: "14px",
                                      lineHeight: "1.6",
                                      whiteSpace: "pre-wrap",
                                      wordBreak: "break-word",
                                    }}
                                  >
                                    {soapNotes.objective || <span style={{ fontStyle: "italic", color: "#9ca3af" }}>No notes recorded</span>}
                                  </div>
                                ) : (
                                  <textarea
                                    type="text"
                                    rows="4"
                                    placeholder="Physical examination findings, vital signs, test results..."
                                    value={soapNotes.objective}
                                    onChange={(e) =>
                                      setSoapNotes({
                                        ...soapNotes,
                                        objective: e.target.value,
                                      })
                                    }
                                  ></textarea>
                                )}
                              </div>
                            </div>
                            <div className="soapNotesRowSec">
                              <div className="sopRowHeader">
                                <h4>Assessment</h4>
                                <p>Your medical assessment</p>
                              </div>
                              <div className="sopRowBody">
                                {!canWriteSoap ? (
                                  <div
                                    style={{
                                      minHeight: "72px",
                                      padding: "12px 16px",
                                      backgroundColor: "#f9fafb",
                                      border: "1px solid #e5e7eb",
                                      borderRadius: "8px",
                                      color: soapNotes.assessment ? "#1f2937" : "#9ca3af",
                                      fontSize: "14px",
                                      lineHeight: "1.6",
                                      whiteSpace: "pre-wrap",
                                      wordBreak: "break-word",
                                    }}
                                  >
                                    {soapNotes.assessment || <span style={{ fontStyle: "italic", color: "#9ca3af" }}>No notes recorded</span>}
                                  </div>
                                ) : (
                                  <textarea
                                    type="text"
                                    rows="4"
                                    placeholder="Diagnosis, differential diagnosis, problem list..."
                                    value={soapNotes.assessment}
                                    onChange={(e) =>
                                      setSoapNotes({
                                        ...soapNotes,
                                        assessment: e.target.value,
                                      })
                                    }
                                  ></textarea>
                                )}
                              </div>
                            </div>
                            <div className="soapNotesRowSec">
                              <div className="sopRowHeader">
                                <h4>Plan</h4>
                                <p>Your treatment plan</p>
                              </div>
                              <div className="sopRowBody">
                                {!canWriteSoap ? (
                                  <div
                                    style={{
                                      minHeight: "72px",
                                      padding: "12px 16px",
                                      backgroundColor: "#f9fafb",
                                      border: "1px solid #e5e7eb",
                                      borderRadius: "8px",
                                      color: soapNotes.plan ? "#1f2937" : "#9ca3af",
                                      fontSize: "14px",
                                      lineHeight: "1.6",
                                      whiteSpace: "pre-wrap",
                                      wordBreak: "break-word",
                                    }}
                                  >
                                    {soapNotes.plan || <span style={{ fontStyle: "italic", color: "#9ca3af" }}>No notes recorded</span>}
                                  </div>
                                ) : (
                                  <textarea
                                    type="text"
                                    rows="4"
                                    placeholder="Treatment plan, medications, follow-up instructions..."
                                    value={soapNotes.plan}
                                    onChange={(e) =>
                                      setSoapNotes({
                                        ...soapNotes,
                                        plan: e.target.value,
                                      })
                                    }
                                  ></textarea>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                        <div id="EmergencyCases" className=" tab-pane fade">
                          <br />
                          <div className="soapNotesSection">
                            <div className="soapNotesHeader mb-3">
                              <div className="soapLeftHeader">
                                <h4>
                                  <img src={Link} /> Prescription
                                </h4>
                              </div>
                            </div>
                            <div className="prescriptionSection">
                              {!canWritePrescription ? (
                                <div style={{ marginTop: "16px" }}>
                                  {medications && medications.filter((m) => m.drugName && m.drugName.trim()).length > 0 ? (
                                    <div
                                      className="table-responsive"
                                      style={{
                                        borderRadius: "8px",
                                        border: "1px solid #e5e7eb",
                                        overflow: "hidden",
                                        backgroundColor: "#fff",
                                      }}
                                    >
                                      <table className="table table-hover mb-0" style={{ fontSize: "14px", verticalAlign: "middle" }}>
                                        <thead style={{ backgroundColor: "#f8fafc", borderBottom: "2px solid #e2e8f0" }}>
                                          <tr>
                                            <th style={{ padding: "12px 16px", color: "#475569", fontWeight: 600, width: "50px" }}>#</th>
                                            <th style={{ padding: "12px 16px", color: "#475569", fontWeight: 600 }}>Medicine / Drug</th>
                                            <th style={{ padding: "12px 16px", color: "#475569", fontWeight: 600, width: "120px" }}>Form</th>
                                            <th style={{ padding: "12px 16px", color: "#475569", fontWeight: 600, width: "120px" }}>Dosage</th>
                                            <th style={{ padding: "12px 16px", color: "#475569", fontWeight: 600, width: "160px" }}>Frequency</th>
                                            <th style={{ padding: "12px 16px", color: "#475569", fontWeight: 600, width: "140px" }}>Duration</th>
                                            <th style={{ padding: "12px 16px", color: "#475569", fontWeight: 600 }}>Instructions</th>
                                          </tr>
                                        </thead>
                                        <tbody>
                                          {medications
                                            .filter((m) => m.drugName && m.drugName.trim())
                                            .map((med, idx) => (
                                              <tr key={med.id || idx} style={{ borderBottom: "1px solid #f1f5f9" }}>
                                                <td style={{ padding: "12px 16px", color: "#64748b" }}>{idx + 1}</td>
                                                <td style={{ padding: "12px 16px", fontWeight: 500, color: "#0f172a" }}>
                                                  {med.drugName}
                                                  {med.snomedCode && (
                                                    <span style={{ display: "block", fontSize: "11px", color: "#64748b" }}>
                                                      SNOMED: {med.snomedCode}
                                                    </span>
                                                  )}
                                                </td>
                                                <td style={{ padding: "12px 16px", color: "#334155" }}>{med.form || "—"}</td>
                                                <td style={{ padding: "12px 16px", color: "#334155" }}>{med.dosage || "—"}</td>
                                                <td style={{ padding: "12px 16px", color: "#334155" }}>{med.frequency || "—"}</td>
                                                <td style={{ padding: "12px 16px", color: "#334155" }}>
                                                  {med.duration ? `${med.duration} ${med.durationUnit || "days"}` : "—"}
                                                </td>
                                                <td style={{ padding: "12px 16px", color: "#334155" }}>{med.instructions || "—"}</td>
                                              </tr>
                                            ))}
                                        </tbody>
                                      </table>
                                    </div>
                                  ) : (
                                    <div
                                      style={{
                                        padding: "36px 20px",
                                        textAlign: "center",
                                        backgroundColor: "#f8fafc",
                                        borderRadius: "8px",
                                        border: "1px dashed #cbd5e1",
                                        color: "#64748b",
                                        fontSize: "14px",
                                      }}
                                    >
                                      <p className="mb-0">No medications prescribed for this visit.</p>
                                    </div>
                                  )}
                                </div>
                              ) : (
                                <form onSubmit={saveMedications}>
                                  {medications.map((medication, index) => (
                                    <div
                                      key={medication.id}
                                      className="addMedicationSection"
                                    >
                                      <div className="d-flex justify-content-between align-items-center mb-3">
                                        <h4>Add Medication</h4>
                                        {medications.length > 1 && (
                                          <button
                                            type="button"
                                            className="customeBtn btnDanger"
                                            onClick={() =>
                                              removeMedication(medication.id)
                                            }
                                            style={{
                                              background: "#dc3545",
                                              border: "none",
                                              color: "white",
                                              padding: "5px 10px",
                                              borderRadius: "4px",
                                            }}
                                          >
                                            Remove
                                          </button>
                                        )}
                                      </div>
                                      <div className="row">
                                        <div className="col-xs-12 col-md-6">
                                          <div className="feilds">
                                            <label>Search Drug</label>
                                            <div className="searchBar">
                                              <MedicineAutocomplete
                                                drugName={medication.drugName}
                                                snomedCode={medication.snomedCode}
                                                snomedDisplay={
                                                  medication.snomedDisplay
                                                }
                                                onChange={(val) => {
                                                  updateMedication(
                                                    medication.id,
                                                    "drugName",
                                                    val,
                                                  );
                                                }}
                                                onSelect={(
                                                  display,
                                                  code,
                                                  snomedDisplay,
                                                ) => {
                                                  setMedications((prev) =>
                                                    prev.map((med) =>
                                                      med.id === medication.id
                                                        ? {
                                                            ...med,
                                                            drugName: display,
                                                            snomedCode: code,
                                                            snomedDisplay:
                                                              snomedDisplay,
                                                          }
                                                        : med,
                                                    ),
                                                  );
                                                }}
                                                onClear={() => {
                                                  setMedications((prev) =>
                                                    prev.map((med) =>
                                                      med.id === medication.id
                                                        ? {
                                                            ...med,
                                                            drugName: "",
                                                            snomedCode: "",
                                                            snomedDisplay: "",
                                                          }
                                                        : med,
                                                    ),
                                                  );
                                                }}
                                              />
                                              {!medication.snomedCode && (
                                                <span className="searchIcon">
                                                  <img src={Search} />
                                                </span>
                                              )}
                                            </div>
                                          </div>
                                        </div>
                                        <div className="col-xs-12 col-md-6">
                                          <div className="feilds">
                                            <label>Form</label>
                                            <select
                                              value={medication.form}
                                              onChange={(e) =>
                                                updateMedication(
                                                  medication.id,
                                                  "form",
                                                  e.target.value,
                                                )
                                              }
                                            >
                                              <option value="Tablet">
                                                Tablet
                                              </option>
                                              <option value="Capsule">
                                                Capsule
                                              </option>
                                              <option value="Syrup">Syrup</option>
                                              <option value="Injection">
                                                Injection
                                              </option>
                                              <option value="Cream">
                                                Cream/Ointment
                                              </option>
                                            </select>
                                          </div>
                                        </div>
                                        <div className="col-xs-12 col-md-6">
                                          <div className="feilds">
                                            <label>Dosage </label>
                                            <input
                                              type="text"
                                              placeholder="e.g., 500mg"
                                              value={medication.dosage}
                                              onChange={(e) =>
                                                updateMedication(
                                                  medication.id,
                                                  "dosage",
                                                  e.target.value,
                                                )
                                              }
                                              required
                                            />
                                          </div>
                                        </div>
                                        <div className="col-xs-12 col-md-6">
                                          <div className="feilds">
                                            <label>Frequency </label>
                                            <select
                                              value={medication.frequency}
                                              onChange={(e) =>
                                                updateMedication(
                                                  medication.id,
                                                  "frequency",
                                                  e.target.value,
                                                )
                                              }
                                              required
                                            >
                                              <option value="">
                                                Select frequency
                                              </option>
                                              <option value="Once daily">
                                                Once daily
                                              </option>
                                              <option value="Twice daily">
                                                Twice daily
                                              </option>
                                              <option value="Thrice daily">
                                                Thrice daily
                                              </option>
                                              <option value="Four times daily">
                                                Four times daily
                                              </option>
                                              <option value="Every 4 hours">
                                                Every 4 hours
                                              </option>
                                              <option value="Every 6 hours">
                                                Every 6 hours
                                              </option>
                                              <option value="Every 8 hours">
                                                Every 8 hours
                                              </option>
                                              <option value="Every 12 hours">
                                                Every 12 hours
                                              </option>
                                              <option value="Once a week">
                                                Once a week
                                              </option>
                                              <option value="Alternate days">
                                                Alternate days
                                              </option>
                                              <option value="At bedtime">
                                                At bedtime
                                              </option>
                                              <option value="As needed (SOS)">
                                                As needed (SOS)
                                              </option>
                                            </select>
                                          </div>
                                        </div>
                                        <div className="col-xs-12 col-md-6">
                                          <div className="feilds">
                                            <label>Duration </label>
                                            <div className="d-flex gap-2">
                                              <input
                                                style={{ flex: 1 }}
                                                type="text"
                                                placeholder="e.g., 7"
                                                value={medication.duration}
                                                onChange={(e) =>
                                                  updateMedication(
                                                    medication.id,
                                                    "duration",
                                                    e.target.value,
                                                  )
                                                }
                                                required
                                              />
                                              <select
                                                style={{
                                                  flex: 1,
                                                  padding: "10px",
                                                  borderRadius: "8px",
                                                  border: "1px solid #dcdcdc",
                                                }}
                                                value={medication.durationUnit}
                                                onChange={(e) =>
                                                  updateMedication(
                                                    medication.id,
                                                    "durationUnit",
                                                    e.target.value,
                                                  )
                                                }
                                              >
                                                <option value="days">Days</option>
                                                <option value="weeks">
                                                  Weeks
                                                </option>
                                                <option value="months">
                                                  Months
                                                </option>
                                              </select>
                                            </div>
                                          </div>
                                        </div>
                                        <div className="col-xs-12 col-md-6">
                                          <div className="feilds">
                                            <label>Instructions </label>
                                            <input
                                              type="text"
                                              placeholder="e.g., After meals"
                                              value={medication.instructions}
                                              onChange={(e) =>
                                                updateMedication(
                                                  medication.id,
                                                  "instructions",
                                                  e.target.value,
                                                )
                                              }
                                              required
                                            />
                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                  ))}

                                  <div className="addMedicationSection">
                                    <div className="row">
                                      <div className="col-xs-12 col-md-6">
                                        <div className="feilds">
                                          <button
                                            type="button"
                                            className="addMedication"
                                            onClick={addNewMedication}
                                          >
                                            + Add Medication
                                          </button>
                                        </div>
                                      </div>
                                      <div className="col-xs-12 col-md-6">
                                        <div className="feilds">
                                          <button
                                            type="submit"
                                            className="addMedication"
                                            disabled={isLoading}
                                            style={{
                                              backgroundColor: "#28a745",
                                              opacity: isLoading ? 0.6 : 1,
                                              cursor: isLoading
                                                ? "not-allowed"
                                                : "pointer",
                                            }}
                                          >
                                            {isLoading
                                              ? "Saving..."
                                              : "Save Medication"}
                                          </button>
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                </form>
                              )}
                            </div>
                          </div>
                        </div>
                        <div id="Consultation" className=" tab-pane fade">
                          <br />
                          <h3>Consultation</h3>
                          <p>
                            Sed ut perspiciatis unde omnis iste natus error sit
                            voluptatem accusantium doloremque laudantium, totam
                            rem aperiam.
                          </p>
                        </div>
                        <div id="MedicalRecords" className=" tab-pane fade">
                          <br />
                          <h3>MedicalRecords</h3>
                          <p>
                            Sed ut perspiciatis unde omnis iste natus error sit
                            voluptatem accusantium doloremque laudantium, totam
                            rem aperiam.
                          </p>
                        </div>
                        <div id="DischargeSummary" className=" tab-pane fade">
                          <br />
                          <h3>DischargeSummary</h3>
                          <p>
                            Sed ut perspiciatis unde omnis iste natus error sit
                            voluptatem accusantium doloremque laudantium, totam
                            rem aperiam.
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <ToastContainer toasts={toasts} removeToast={removeToast} />
    </>
  );
};

export default OPDDetails;
